// src/app/core/guards/role.guard.ts
import { inject } from '@angular/core';
import {
  CanActivateFn, CanMatchFn, ActivatedRouteSnapshot, RouterStateSnapshot,
  Route, Router, UrlTree, UrlSegment
} from '@angular/router';
import { take, map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { AppUser } from '../models/user';

function hasMatchingRole(expected: readonly string[] | undefined, userRoles: AppUser['roles']): boolean {
  if (!expected || expected.length === 0) return true;
  const set = Array.isArray(userRoles) ? new Set(userRoles) : new Set(Object.keys(userRoles ?? {}));
  return expected.some(r => set.has(r));
}

function buildGuardResult(
  expectedRoles: readonly string[] | undefined,
  redirectUrl?: string
): Observable<boolean | UrlTree> {
  const auth = inject(AuthService);
  const router = inject(Router);

  return auth.appUser$.pipe(
    take(1),
    map((user: AppUser | null) => {
      const ok = !!user && user.estado !== false && hasMatchingRole(expectedRoles, user.roles);
      return ok ? true : router.createUrlTree(['/login'], { queryParams: { denied: 1 } });
    })
  );
}

export const roleCanActivate: CanActivateFn = (route: ActivatedRouteSnapshot, _state: RouterStateSnapshot) => {
  const expected = route.data?.['expectedRoles'] as string[] | undefined;
  const redirectUrl = route.url?.map(s => s.path).join('/') || route.routeConfig?.path || '/';
  return buildGuardResult(expected, redirectUrl);
};

export const roleCanMatch: CanMatchFn = (route: Route, segments: UrlSegment[]) => {
  const expected = route.data?.['expectedRoles'] as string[] | undefined;
  const redirectUrl = segments?.map(s => s.path).join('/') || route.path || '/';
  return buildGuardResult(expected, redirectUrl);
};
